import React from 'react';
import { FiCalendar, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';

const numberFormatter = new Intl.NumberFormat('en-US');
const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

export const getHostDisplayName = (host) => {
  const fullName = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return fullName || host?.companyName || host?.email || 'Unnamed host';
};

const getInitials = (host) =>
  getHostDisplayName(host)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'H';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatAddress = (address) => {
  if (!address) return '';
  const line = [address.line1, address.line2].filter(Boolean).join(' ');
  const locality = [address.city, address.state].filter(Boolean).join(', ');
  return [line, locality, address.zipCode].filter(Boolean).join(' · ');
};

// Earnings payload shape: { [bucket]: { earnings: number[] } }
export const sumEarnings = (earnings) => {
  if (!earnings || typeof earnings !== 'object') return 0;
  return Object.values(earnings).reduce((total, bucket) => {
    const values = Array.isArray(bucket?.earnings) ? bucket.earnings : [];
    return total + values.reduce((sum, value) => sum + Number(value || 0), 0);
  }, 0);
};

function Pill({ label, tone = 'zinc' }) {
  const tones = {
    zinc: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    sky: 'bg-sky-50 text-sky-700 ring-sky-200',
  };
  return (
    <span className={joinClasses('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1', tones[tone] || tones.zinc)}>
      {label}
    </span>
  );
}

function StatTile({ label, value, sublabel, loading }) {
  return (
    <div className="flex h-[60px] flex-col justify-center rounded-xl border border-zinc-200 bg-white px-3">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        <span className={joinClasses('text-[17px] font-semibold leading-none tracking-tight tabular-nums text-zinc-900', loading ? 'opacity-50' : '')}>
          {value}
        </span>
        {sublabel ? <span className="truncate text-[10.5px] text-zinc-500">{sublabel}</span> : null}
      </div>
    </div>
  );
}

function ContactChip({ icon: Icon, children, href }) {
  const className = 'inline-flex max-w-full items-center gap-1.5 text-[13px] text-zinc-600';
  const content = (
    <>
      <Icon className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <span className="truncate">{children}</span>
    </>
  );
  return href ? (
    <a href={href} className={joinClasses(className, 'hover:text-zinc-900')}>{content}</a>
  ) : (
    <span className={className}>{content}</span>
  );
}

/**
 * Identity header + compact stat row for a single host.
 */
export default function HostOverviewCard({
  host,
  loading,
  propertyCount = 0,
  connectedPmsCount = 0,
  completedEarnings = 0,
  pendingEarnings = 0,
  earningsLoading = false,
}) {
  if (loading && !host) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-28 rounded-2xl border border-zinc-200 bg-white" />
        <div className="grid grid-cols-2 gap-2 laptop:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[60px] rounded-xl border border-zinc-200 bg-white" />)}
        </div>
      </div>
    );
  }

  const active = Boolean(host?.isVerified);
  const conciergeOn = host?.guestConciergeServicesEnabled !== false;
  const displayName = getHostDisplayName(host);
  const company = host?.companyName?.trim();
  const address = formatAddress(host?.address);

  return (
    <div className="space-y-3">
      <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start gap-4">
          <div className={joinClasses(
            'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-semibold',
            active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
          )}>
            {getInitials(host)}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold tracking-tight text-zinc-900">{displayName}</h2>
              {company && company !== displayName ? <span className="truncate text-sm text-zinc-500">{company}</span> : null}
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Pill label={active ? 'Active' : 'Inactive'} tone={active ? 'emerald' : 'amber'} />
              <Pill label={host?.isVerified ? 'Verified' : 'Unverified'} tone={host?.isVerified ? 'emerald' : 'amber'} />
              <Pill label={host?.isOnBoarded ? 'Onboarded' : 'Needs onboarding'} tone={host?.isOnBoarded ? 'sky' : 'amber'} />
              <Pill label={`Guest concierge ${conciergeOn ? 'on' : 'off'}`} tone={conciergeOn ? 'emerald' : 'zinc'} />
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {host?.email ? <ContactChip icon={FiMail} href={`mailto:${host.email}`}>{host.email}</ContactChip> : null}
              {host?.phoneNumber ? <ContactChip icon={FiPhone} href={`tel:${host.phoneNumber}`}>{host.phoneNumber}</ContactChip> : null}
              {address ? <ContactChip icon={FiMapPin}>{address}</ContactChip> : null}
              <ContactChip icon={FiCalendar}>Signed up {formatDate(host?.createdAt)}</ContactChip>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-2 laptop:grid-cols-4">
        <StatTile label="Properties" value={numberFormatter.format(propertyCount)} sublabel="active listings" />
        <StatTile
          label="Active bookings"
          value={moneyFormatter.format(pendingEarnings)}
          sublabel="pending orders"
          loading={earningsLoading}
        />
        <StatTile
          label="Last 30 days"
          value={moneyFormatter.format(completedEarnings)}
          sublabel="completed orders"
          loading={earningsLoading}
        />
        <StatTile
          label="PMS connections"
          value={numberFormatter.format(connectedPmsCount)}
          sublabel={connectedPmsCount ? 'connected' : 'none detected'}
        />
      </section>
    </div>
  );
}
